<script setup lang="ts">
import { computed, resolveComponent } from 'vue';
import { THEME } from '@pkg/config/misc/theme';
import { cn } from '@pkg/ui/lib/utils';
import { type Image } from '@sanity/types';
import type { ModularBlock } from '@pkg/sanity-utils/types/modularContentBlocks';
import type { VideoType } from '@pkg/sanity-utils/constants/video';
import Video from '@pkg/ui/components/base/Video.vue';
import ModularBlocksRenderer from '@pkg/modular-blocks/frontend/ModularBlocksRenderer.vue';
import { useResolvedSectionTheme } from '@pkg/modular-blocks/frontend/useResolvedSectionTheme';
import { subHeroSectionVariants } from './variants';

interface MediaImage {
  mediaType: 'image';
  image?: Image | null | undefined;
  video: never;
}

interface MediaVideo {
  mediaType: 'video';
  image: never;
  video?: {
    url?: string;
    webm?: string;
    ogg?: string;
    fallbackImage?: Image;
    controls?: boolean;
    autoplay?: boolean;
    videoType?: VideoType;
  };
}

type Props = {
  theme: THEME;
  content?: Array<ModularBlock>;
  variant?: NonNullable<
    Parameters<typeof subHeroSectionVariants>[0]
  >['variant'];
  mediaType?: 'image' | 'video';
} & (MediaImage | MediaVideo);

const props = defineProps<Props>();

const resolvedTheme = useResolvedSectionTheme(() => props.theme);
const subHeroThemedStyle = computed(() => resolvedTheme.value === THEME.APP);

/** Host app registers these (PartialSection, BaseImage). */
const PartialSection = resolveComponent('PartialSection');
const BaseImage = resolveComponent('BaseImage');
</script>

<template>
  <component
    :is="PartialSection"
    :theme="resolvedTheme"
    :class="subHeroSectionVariants({ variant })"
  >
    <div :class="cn('c-sub-hero-section__wrapper wrapper', { 'c-sub-hero-section__wrapper--app': subHeroThemedStyle })">
      <div :class="cn('c-sub-hero-section__content')">
        <slot name="content">
          <ModularBlocksRenderer :blocks="content || []" />
        </slot>
      </div>
      <slot v-if="variant !== 'mediaNone'" name="media">
        <div
          :class="
            cn('c-sub-hero-section__media-container', {
              'wrapper wrapper--content': variant === 'mediaCenter',
            })
          "
        >
          <slot v-if="props.mediaType === 'video'">
            <component
              :is="BaseImage"
              :image="props.video?.fallbackImage"
              loading="eager"
              :width="792"
              class="c-sub-hero-section__media"
              style="position: absolute"
            />
            <Video
              v-if="props.video?.url"
              :url="props.video?.url"
              :webm="props.video?.webm"
              :ogg="props.video?.ogg"
              :video-type="props.video?.videoType"
              :autoplay="props.video?.autoplay"
              :controls="props.video?.controls"
              class="c-sub-hero-section__media"
            >
              <template v-if="props.video?.fallbackImage" #cover>
                <component :is="BaseImage" :image="props.video.fallbackImage" />
              </template>
            </Video>
          </slot>

          <slot v-else>
            <component
              :is="BaseImage"
              :image="props.image"
              loading="eager"
              :width="792"
              class="c-sub-hero-section__media"
            />
          </slot>
        </div>
      </slot>
    </div>
    <div class="c-sub-hero-section__gap" />
  </component>
</template>

<style scoped>
.c-sub-hero-section {
  &__wrapper {
    display: flex;
    flex-direction: column-reverse;
    justify-content: center;
    align-items: center;
    gap: 2rem;
    padding-top: theme('spacing.56');

    &--app {
      padding-top: 0;
    }

    @screen desk-small {
      flex-direction: row;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  &__media {
    object-fit: cover;
  }

  &--media-right {
    & .c-sub-hero-section__wrapper {
      justify-content: space-between;
    }

    & .c-sub-hero-section__content {
      max-width: 588px;
    }

    & .c-sub-hero-section__media {
      max-height: 220px;
      max-width: min(100%, 588px);
      margin-left: auto;
      margin-right: auto;

      @screen desk-small {
        width: 486px;
        height: 486px;
        max-height: none;
        max-width: unset;
        margin-left: unset;
        margin-right: unset;
      }
    }
  }

  &--media-center {
    --sub-hero-image-height: 214px;
    @screen tablet-wide {
      --sub-hero-image-height: 356px;
    }

    &:has(+ [data-theme="light"]) {
      --gap-color: var(--theme-background-color-light);
    }

    &:has(+ [data-theme="light-alt"]) {
      --gap-color: var(--theme-background-color-light-alt);
    }

    &:has(+ [data-theme="dark"]) {
      --gap-color: var(--theme-background-color-dark);
    }

    &:has(+ [data-theme="dark-alt"]) {
      --gap-color: var(--theme-background-color-dark-alt);
    }

    &:has(+ [data-theme="app"]) {
      --gap-color: var(--theme-heading-color-app);
    }

    /* has-sibling-with-different-theme (inlined — was web CSS mixin) */
    &[data-theme="app"]:has(+ :not([data-theme="app"])),
    &[data-theme="light"]:has(+ :not([data-theme="light"])),
    &[data-theme="light-alt"]:has(+ :not([data-theme="light-alt"])),
    &[data-theme="dark"]:has(+ :not([data-theme="dark"])),
    &[data-theme="dark-alt"]:has(+ :not([data-theme="dark-alt"])) {
      padding-bottom: 0;
    }

    & .c-sub-hero-section__gap {
      height: calc((var(--sub-hero-image-height) / 2));
      background-color: var(--gap-color);
    }

    & .c-sub-hero-section__wrapper {
      flex-direction: column;
      justify-content: space-between;
      position: relative;

      gap: 2rem;

      @screen desk-small {
        gap: 3.5rem;
      }
    }

    & .c-sub-hero-section__media-container {
      display: flex;
      justify-content: center;
      position: relative;
      overflow: visible;
      height: calc(var(--sub-hero-image-height) / 2);
    }

    & .c-sub-hero-section__media {
      height: var(--sub-hero-image-height);
      position: absolute;
      width: 100%;

      @screen desk-small {
        height: var(--sub-hero-image-height);
      }
    }
  }
}
</style>
